import { useTranslation } from 'react-i18next';
import { PageTransition, StaggerItem } from '../app/page-transition.js';
import { TableOfContents } from '../../components/ui/table-of-contents/table-of-contents.js';

type ChangelogEntry = { version: string; date: string; summaryKey: string };

// Newest-first source order (asserted in test).
const CHANGELOG_ENTRIES: ReadonlyArray<ChangelogEntry> = [
  {
    version: 'v1.3.0',
    date: '2026-04-14',
    summaryKey: 'changelog.entry.v1-3-0.summary',
  },
  {
    version: 'v1.2.0',
    date: '2026-04-11',
    summaryKey: 'changelog.entry.v1-2-0.summary',
  },
  {
    version: 'v1.1.0',
    date: '2026-04-08',
    summaryKey: 'changelog.entry.v1-1-0.summary',
  },
  {
    version: 'v1.0.0',
    date: '2026-04-01',
    summaryKey: 'changelog.entry.v1-0-0.summary',
  },
  {
    version: 'v0.9.0',
    date: '2026-03-15',
    summaryKey: 'changelog.entry.v0-9-0.summary',
  },
];

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

export function ChangelogPage() {
  const { t } = useTranslation('content');

  const tocItems = CHANGELOG_ENTRIES.map((entry) => ({
    id: `release-${entry.version.replaceAll('.', '-')}`,
    label: entry.version,
  }));

  return (
    <PageTransition>
      <div className="flex w-full justify-between gap-12 px-6 pb-32 pt-16 standard:px-10">
        <div className="min-w-0 flex-1 wide:max-w-[960px]">
          <StaggerItem>
            <p className="font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
              Changelog
            </p>
            <h1 className="mt-4 max-w-[20ch] font-sans text-[clamp(36px,6vw,56px)] font-light leading-[1.05] tracking-[-0.03em] text-primary">
              {t('changelog.heading')}
            </h1>
            <p className="mb-14 mt-4 max-w-[60ch] text-[16px] leading-[1.6] text-secondary">
              Releases shipped to production. The MCP server auto-updates on
              <code className="mx-1 rounded-[3px] border border-white/[0.06] bg-[#0c0c0c] px-1.5 py-0.5 font-mono text-[12.5px] text-accent-cyan">
                npx
              </code>
              ; install globally to pin a version.
            </p>
          </StaggerItem>

          <StaggerItem>
            <div className="flex flex-col">
              {CHANGELOG_ENTRIES.map((entry, idx) => (
                <article
                  key={entry.version}
                  id={`release-${entry.version.replaceAll('.', '-')}`}
                  data-testid="changelog-entry"
                  data-version={entry.version}
                  className={`scroll-mt-24 grid grid-cols-[120px_1fr] gap-8 py-7 ${
                    idx === 0 ? 'border-t border-white/[0.06]' : 'border-t border-white/[0.06]'
                  } ${idx === CHANGELOG_ENTRIES.length - 1 ? 'border-b' : ''}`}
                >
                  <div>
                    <p className="m-0 font-mono text-[13px] font-medium text-primary">
                      {entry.version}
                    </p>
                    <time
                      dateTime={entry.date}
                      className="mt-1 block font-mono text-[11px] uppercase tracking-[0.08em] text-muted"
                    >
                      {dateFormatter.format(new Date(entry.date))}
                    </time>
                  </div>
                  <p className="m-0 max-w-[60ch] text-[15px] leading-[1.6] text-secondary">
                    {t(entry.summaryKey)}
                  </p>
                </article>
              ))}
            </div>
          </StaggerItem>
        </div>
        <TableOfContents items={tocItems} title="Releases" />
      </div>
    </PageTransition>
  );
}
